import { Box, Grid } from '@mui/material';
import moment from 'moment';
import React, { Fragment, useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmployeeSelect } from '~/components/GenericForm/EmployeeSelect';
import { useApplicationConfig } from '~/data/applicationConfig';
import { TabModules, useModulesConfig } from '~/data/company';
import { Enum, useAllEnums } from '~/data/enums';
import { RewardingTabs } from '~/data/tabs';
import {
  PersonnelGroupValues,
  PersonnelGroups,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import {
  ChangedStartDateState,
  Form,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import {
  LegacyAutocompleteField,
  LegacyDateField,
  LegacyFieldWrapper,
  LegacyInputField,
  LegacyMultiCheckbox,
} from '~/legacy-components/LegacyForms';
import { AppModules } from '~/services/Permissions';

import {
  EmploymentDetailsCustomData,
  EmploymentStatusComponent,
  JobGradeComponent,
} from './components';
import { useSxStyles } from './useStyles';

type Props = {
  scheduleFormMethods: UseFormReturn<Form>;
  employmentStatuses: Enum[];
  jobGrades: Enum[];
  getPersonnelGroupOptions: (
    groupNumber: number,
    personnelGroupValues: PersonnelGroupValues,
  ) => Enum[];
  index: number;
  fComMaxPersonnelGroups: number;
  newEmpl: boolean;
  setChangedStartDate: React.Dispatch<
    React.SetStateAction<ChangedStartDateState>
  >;
  allResponsibleHRIds: number[];
  pendingProposal: boolean;
  inProgressPropStartDate?: number;
  hrSelectorDisable: boolean;
  newEngine: boolean;
};

export const FormComponent = (props: Props) => {
  const {
    scheduleFormMethods,
    getPersonnelGroupOptions,
    index,
    fComMaxPersonnelGroups,
    newEmpl,
    setChangedStartDate,
    allResponsibleHRIds,
    pendingProposal,
    inProgressPropStartDate,
    hrSelectorDisable,
    newEngine,
  } = props;

  const {
    formState: { errors },
    control,
    getValues,
    setValue,
  } = scheduleFormMethods;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { allEnums } = useAllEnums();

  const { applicationConfig } = useApplicationConfig();
  const { is7DaysEnabled, vacationCalculationDayOptions } = useMemo(() => {
    const is7DaysEnabled =
      applicationConfig?.fComConfig?.absConfig?.fComEnable7DaysVacationRule ??
      false;

    const selectedDaysRule = getValues(
      //@ts-ignore
      `schedule.${index}.employmentDetails.fEmplDetailVacBalCalcSixDays`,
    );

    const vacationCalculationDayOptions =
      is7DaysEnabled || selectedDaysRule === 7 ? [5, 6, 7] : [5, 6];

    return {
      is7DaysEnabled,
      vacationCalculationDayOptions,
    };
  }, [index, getValues, applicationConfig]);

  const { isModuleTabEnabled, isModuleEnabled } = useModulesConfig();
  const { isWorkTimeEnabled } = useMemo(() => {
    return {
      isWorkTimeEnabled:
        isModuleEnabled(AppModules.WORK_TIME_REPORTING) &&
        isModuleTabEnabled(TabModules.REWARDING, RewardingTabs.WorkTime),
    };
  }, [isModuleTabEnabled, isModuleEnabled]);

  const getPersonnelGroupName = (number: number) => {
    const index = number - 1;
    return allEnums.PERSONNEL_GROUP_NAME[index].name;
  };

  const personnelGroupIndexNumbers: number[] = Array.from(
    Array(fComMaxPersonnelGroups).keys(),
  );

  const startDateMinDate = useMemo(() => {
    const employmentStartDate = getValues(
      //@ts-ignore
      `schedule.${index}.employment.fEmplDateStart`,
    );

    if (inProgressPropStartDate && employmentStartDate) {
      return moment(inProgressPropStartDate).isBefore(employmentStartDate)
        ? employmentStartDate
        : moment(inProgressPropStartDate);
    } else if (!inProgressPropStartDate && employmentStartDate) {
      return employmentStartDate;
    } else if (!employmentStartDate && inProgressPropStartDate)
      return moment(inProgressPropStartDate);
    else return undefined;
  }, [getValues, inProgressPropStartDate, index]);

  const RenderPersonnelGroupField = ({
    groupNumber,
  }: {
    groupNumber: number;
  }) => {
    //@ts-ignore
    const emplDetailData = useWatch({
      control,
      name: `schedule.${index}.employmentDetails`,
    });
    const personnelGroupValues: PersonnelGroupValues = emplDetailData
      ? personnelGroupIndexNumbers.reduce(
          (acc, cur) => ({
            ...acc,
            [`PERSONNEL_GROUP_${cur}`]:
              emplDetailData[
                `fEmplDetailPersonnelGroup${cur}` as keyof PersonnelGroups
              ],
          }),
          {},
        )
      : {};
    const fieldName =
      `fEmplDetailPersonnelGroup${groupNumber}` as keyof PersonnelGroups;
    const fieldOptions = getPersonnelGroupOptions(
      groupNumber,
      personnelGroupValues,
    );
    return (
      <Grid item>
        <Controller
          control={control}
          name={`schedule.${index}.employmentDetails.${fieldName}`}
          render={({ field: { onChange, value } }) => (
            <LegacyAutocompleteField
              options={fieldOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              onChange={(_, value) => onChange(value?.code || '')}
              // @ts-ignore
              value={fieldOptions.find((o) => o.code === value) || null}
              sx={sxStyles.selectStyle}
              fieldError={
                errors.schedule?.[index]?.employmentDetails?.[fieldName]
              }
              label={getPersonnelGroupName(groupNumber)}
              disabled={pendingProposal}
            />
          )}
        />
      </Grid>
    );
  };

  const RenderPersonnelGroupFields = () => {
    return (
      <>
        {personnelGroupIndexNumbers.map((number) => (
          <React.Fragment key={number}>
            <RenderPersonnelGroupField groupNumber={number + 1} />
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <Grid container columnSpacing={1}>
      <Grid item>
        {/* @ts-ignore */}
        <EmploymentStatusComponent
          control={control}
          index={index}
          allEnums={allEnums}
          getValues={getValues}
          setValue={setValue}
          errors={errors}
          disabled={pendingProposal}
        />
      </Grid>
      <Grid item>
        <JobGradeComponent
          control={control}
          index={index}
          allEnums={allEnums}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          disabled={pendingProposal}
        />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={`schedule.${index}.employmentDetails.fEmplDetailTaxNumber`}
          render={({ field: { onChange, value } }) => (
            <LegacyInputField
              value={value}
              sx={sxStyles.selectStyle}
              onChange={onChange}
              label={t('fEmplDetailTaxNumber')}
              disabled={pendingProposal}
            />
          )}
        />
      </Grid>
      <RenderPersonnelGroupFields />
      <EmploymentDetailsCustomData
        control={control}
        index={index}
        allEnums={allEnums}
        pendingProposal={pendingProposal}
      />
      <Grid item>
        <Controller
          name={`schedule.${index}.employmentDetails.fEmplDetailVacBalCalcSixDays`}
          control={control}
          render={({ field: { onChange, value } }) => {
            const renderOption = (option: number) => {
              const optionId = `idVacBalCal${option}`;

              return (
                <Fragment key={`balance-calculation-option-${option}`}>
                  <input
                    id={optionId}
                    type="radio"
                    value={option}
                    onChange={(event) => {
                      const value = parseInt(event.target.value);
                      if (!isNaN(value)) {
                        onChange(value);
                      }
                    }}
                    checked={value === option}
                    disabled={
                      pendingProposal || (option === 7 && !is7DaysEnabled)
                    }
                  />
                  <Box
                    component={'label'}
                    htmlFor={optionId}
                    sx={sxStyles.radioLabel}
                  >
                    {option}
                  </Box>
                </Fragment>
              );
            };

            return (
              <LegacyFieldWrapper label={t('laVacBalCal')}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: 0.5,
                  }}
                >
                  {vacationCalculationDayOptions.map(renderOption)}
                </Box>
              </LegacyFieldWrapper>
            );
          }}
        />
      </Grid>
      {isWorkTimeEnabled && (
        <Grid item>
          <Controller
            name={`schedule.${index}.employmentDetails.fEmplDetailCalcHoursBalance`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <LegacyMultiCheckbox
                fieldName={'fEmplDetailCalcHoursBalance'}
                value={value}
                onChange={onChange}
                options={[{ value: 'true' }]}
                fieldError={
                  errors.schedule?.[index]?.employmentDetails
                    ?.fEmplDetailCalcHoursBalance
                }
                label={t('laCalcHoursBalance')}
                disabled={pendingProposal}
              />
            )}
          />
        </Grid>
      )}
      <Grid item>
        <Controller
          name={`schedule.${index}.employmentDetails.fEmplDetailStartDate`}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <LegacyDateField
              label={t('laStartDate')}
              disabled={newEmpl || pendingProposal}
              minDate={startDateMinDate}
              maxDate={
                moment(
                  getValues(
                    `schedule.${index}.employment.fEmplLastDateEmployment`,
                  ),
                ) || undefined
              }
              onChange={(v: moment.Moment | null) => {
                onChange(v || null);
                setChangedStartDate((activeValue) => ({
                  ...activeValue,
                  emplDetailsStartDate: true,
                }));
              }}
              value={value}
              fieldError={
                errors.schedule?.[index]?.employmentDetails
                  ?.fEmplDetailStartDate
              }
              required={true}
            />
          )}
        />
      </Grid>
      {index === 0 && newEngine && (
        <Grid item xs={6} sx={{ mt: 2 }}>
          <Controller
            name={`schedule.${index}.employmentDetails.fEmplDetailProposalHrId`}
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <EmployeeSelect
                  FormControlProps={{ fullWidth: true, error: !!error }}
                  label={t('laHRResponsible')}
                  value={value || ''}
                  onChange={onChange}
                  optionEmployeeIds={allResponsibleHRIds}
                  fullWidth={true}
                  required={true}
                  disabled={pendingProposal || hrSelectorDisable}
                  size="small"
                />
              );
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};
